import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, MessageSquare, Search, Calendar, Users } from "lucide-react";

function formatWhen(when) {
  if (!when) return null;
  const d = new Date(when);
  if (isNaN(d.getTime())) return when;
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}


export default function InteractionsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.getInteractions(token).then((d) => setItems(d.items || [])).finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.subject || "").toLowerCase().includes(q) ||
      (i.contacts_involved || []).some((c) => c.toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <div className="space-y-6 animate-in" data-testid="interactions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><MessageSquare className="w-7 h-7" />Interacciones</h1>
          <p className="text-muted-foreground mt-1">Histórico de contactos con el Ecosistema (solo lectura, sincronizado desde Airtable)</p>
        </div>
        <Badge variant="outline" data-testid="interactions-count">{items.length} interacciones</Badge>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por asunto o contacto..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="interactions-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="interactions-empty">Sin interacciones para mostrar.</p>
      ) : (
        <div className="space-y-3" data-testid="interactions-list">
          {filtered.map((i) => (
            <Card key={i.id} data-testid={`interaction-card-${i.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="font-semibold">{i.subject}</p>
                  {i.type && <Badge variant="outline">{i.type}</Badge>}
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {i.when && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatWhen(i.when)}</span>}
                  {(i.contacts_involved || []).length > 0 && (
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{i.contacts_involved.join(", ")}</span>
                  )}
                </div>
                {i.notes && <p className="text-sm whitespace-pre-wrap">{i.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
